import React, { createContext, useContext, useState, useEffect } from 'react';
import { OrderExecutionEngine } from '../../api/OrderExecutionEngine';

const OrderNotificationContext = createContext();

export const useOrderNotification = () => useContext(OrderNotificationContext);

export const OrderNotificationProvider = ({ children }) => {
    const [newOrder, setNewOrder] = useState(null);
    const [pendingCount, setPendingCount] = useState(0);
    const [lastOrderId, setLastOrderId] = useState(null);

    useEffect(() => {
        // Limpa qualquer snooze pendente assim que o usuário (re)entra no sistema
        localStorage.removeItem('mos_limbo_snooze');

        // Solicita permissão para notificações do Chrome ao carregar o sistema
        if ("Notification" in window && Notification.permission !== "granted" && Notification.permission !== "denied") {
            Notification.requestPermission();
        }

        const playNotificationSound = () => {
            try {
                const AudioContext = window.AudioContext || window.webkitAudioContext;
                if (!AudioContext) return;
                const ctx = new AudioContext();
                
                // Sintetiza um som duplo suave (Estilo WhatsApp / Notificação VIP)
                const playTone = (freq, startTime, duration) => {
                    const osc = ctx.createOscillator();
                    const gain = ctx.createGain();
                    osc.connect(gain);
                    gain.connect(ctx.destination);
                    osc.type = 'sine';
                    osc.frequency.setValueAtTime(freq, ctx.currentTime + startTime);
                    gain.gain.setValueAtTime(0, ctx.currentTime + startTime);
                    gain.gain.linearRampToValueAtTime(0.5, ctx.currentTime + startTime + 0.05);
                    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + startTime + duration);
                    osc.start(ctx.currentTime + startTime);
                    osc.stop(ctx.currentTime + startTime + duration);
                };

                playTone(880, 0, 0.2);     // Tom 1
                playTone(1108.73, 0.15, 0.4); // Tom 2 (Subida)
            } catch(e) { console.warn("Audio block", e); }
        };

        const checkNewOrders = async () => {
            try {
                // Verificação do Snooze (5 minutos de paz)
                const snoozeTime = parseInt(localStorage.getItem('mos_limbo_snooze') || '0');
                if (Date.now() < snoozeTime) return;

                // Check for pending orders no Limbo (Triagem)
                const pendingOrders = await OrderExecutionEngine.listarNovos();
                
                if (pendingOrders && pendingOrders.length > 0) {
                    setPendingCount(pendingOrders.length);
                    setNewOrder(currentNewOrder => {
                        // Se já tem um alarme tocando na tela, não faz nada
                        if (currentNewOrder) return currentNewOrder;

                        // Se a tela está livre, PAGA O PRIMEIRO PEDIDO DO LIMBO E GERA O ALARME!
                        const latestOrder = pendingOrders[0];
                        
                        playNotificationSound();
                        
                        // Dispara a Notificação Nativa
                        const clienteNome = latestOrder.empresa?.razaoSocial || latestOrder.clienteNome || 'Cliente Desconhecido';
                        const totalMonthly = latestOrder.itens?.reduce((acc, it) => acc + ((it.quantidade || 0) * (it.precoUnitario || 0)), 0) || 0;
                        const valorFormatado = totalMonthly > 0 ? `\nReceita: ${totalMonthly.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}` : '';

                        if ("Notification" in window && Notification.permission === "granted") {
                            const title = `🚨 Temos ${pendingOrders.length} pedidos novos!`;
                            const options = {
                                body: `Cliente mais recente: ${clienteNome}${valorFormatado}\nVá assumi-los!`,
                                icon: '/logo.png',
                                vibrate: [300, 100, 300, 100, 300],
                                requireInteraction: true
                            };

                            if ('serviceWorker' in navigator) {
                                navigator.serviceWorker.ready.then(reg => {
                                    reg.showNotification(title, options);
                                }).catch(() => new Notification(title, options));
                            } else {
                                new Notification(title, options);
                            }
                        }

                        return latestOrder;
                    });
                } else {
                    setPendingCount(0);
                }
            } catch (err) {
                console.error("Erro no polling de notificação de pedidos:", err);
            }
        };

        const interval = setInterval(checkNewOrders, 30000);
        checkNewOrders();

        return () => clearInterval(interval);
    }, []);

    const dismissPopup = () => {
        // Ativa Snooze de 5 minutos (300.000 ms) ao clicar "Já vi e vou fazer"
        localStorage.setItem('mos_limbo_snooze', Date.now() + 5 * 60 * 1000);
        setNewOrder(null);
    };

    return (
        <OrderNotificationContext.Provider value={{ newOrder, pendingCount, dismissPopup }}>
            {children}
        </OrderNotificationContext.Provider>
    );
};
